import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {ProjectLopaPositionsComponent} from '@catdetails/components/project-lopa-positions/project-lopa-positions.component';
import {UserNotesModule} from '@catmodules/widgets/user-notes/user-notes.module';
import {StoreModule} from '@ngrx/store';

import {CoreModule} from '@catcore/core.module';
import {UIBaseModule} from '@catui/ui-base.module';
import {UIFormModule} from '@catuiform/ui-form.module';
import {ColorPickerModule} from 'ngx-color-picker';
import {TableModule} from 'primeng/table';

import * as reducers from './reducers';

import {
  ProjectDetailsComponent,
  ProjectDiagramComponent,
  ProjectPropertiesComponent,
  SwitchLogicSelectComponent,
} from './components/';

import {FeatureComponent} from '@catdetails/components/feature/feature.component';
import {FeaturesComponent} from '@catdetails/components/features/features.component';
import {CreateGuiZoneComponent} from '@catdetails/components/gui-zone/create-gui-zone/create-gui-zone.component';
import {GuiZonesComponent} from '@catdetails/components/gui-zone/gui-zones/gui-zones.component';
import {ProjectFeaturesLandingComponent} from '@catdetails/components/landing-pages/project-features-landing/project-features-landing.component';
import {ProjectParametersLandingComponent} from '@catdetails/components/landing-pages/project-parameters-landing/project-parameters-landing.component';
import {ProjectUnitLandingComponent} from '@catdetails/components/landing-pages/project-unit-landing/project-unit-landing.component';
import {ProjectUnitsLandingComponent} from '@catdetails/components/landing-pages/project-units-landing/project-units-landing.component';
import {ProjectUnitsComponent} from '@catdetails/components/project-units/project-units.component';
import {GuiZoneModule} from '@catmodules/models/gui-zone/gui-zone.module';
import {PaZoneModule} from '@catmodules/models/pa-zone/pa-zone.module';
import {ParametersModule} from '@catmodules/models/parameters/parameters.module';
import {ProjectFeatureModule} from '@catmodules/models/project-feature/project-feature.module';
import {ProjectMediaSourcesModule} from '@catmodules/models/project-media-sources/project-media-sources.module';
import {ProjectServiceFilesModule} from '@catmodules/models/project-service-files/project-service-files.module';
import {FeatureUnitsModule} from '@catmodules/models/project-units/project-units.module';
import {ServicesModule} from '@catmodules/models/services/services.module';
import {NetworkInterfacesModule} from "@catmodules/models/network-interfaces/network-interfaces.module";
import {ExplorerModule} from '@catmodules/widgets/explorer/explorer.module';
import {SlideInModalModule} from '@catmodules/widgets/slide-in-modal/slide-in-modal.module';
import {
  ConfirmationService,
  ConfirmDialogModule,
  DialogModule,
  DataTableModule,
  InputTextModule,
} from 'primeng/primeng';
import {CurrentProjectResolve} from './resolve/current-project.resolve';
import {ProjectResolve} from './resolve/project.resolve';
import { UploadFilesComponent } from './components/userdata-file/userdata-files/upload-files.component';
import { UploadFilesService } from "@catdetails/services/upload-files.service";
import {
  GuiZoneService,
  GuiZonesService,
  ProjectDetailsFeaturesService,
  ProjectDetailsService,
  ProjectPropertiesService,
  SBDService,
} from './services';
import { TestRackComponent } from './components/test-rack/test-rack.component';
import {TestRackService} from "@catdetails/../models/test-rack/services/test-rack.service";
import {TestRackModule} from "@catmodules/models/test-rack/test-rack.module";
import {ProjectOptionTokensModule} from "@catmodels/project-option-tokens/project-option-tokens.module";
import {ProjectParametersModule} from "@catmodels/project-parameters/project-parameters.module";
import {GgwDevZoneAssignmentModule} from "@catmodels/ggw-dev-zone-assignment/ggw-dev-zone-assignment.module";
import {ProjectEvidFunctionsModule} from "@catmodels/project-evid-functions/project-evid-functions.module";
import {GgwComponent} from "@catdetails/components/ggw/ggw.component";
import {GgwService} from "@catdetails/services/ggw.service";
import {GgwProfilesModule} from "@catmodels/ggw-profiles/ggw-profiles.module";
import {GgwDynamicRoutesModule} from "@catmodels/ggw-dynamic-routes/ggw-dynamic-routes.module";
import {ProjectPartnumbersModule} from "@catmodels/project-partnumbers/project-partnumbers.module";
import {ProjectSpeakerSetupModule} from "@catmodels/project-speaker-setup/project-speaker-setup.module";
import {UserdataGenerationComponent} from './components/userdata-generation/userdata-generation.component';
import {UserdataGenerationService} from "@catdetails/../models/userdata-generation/services/userdata-generation.service";
import {UserdataGenerationModule} from "@catmodules/models/userdata-generation/userdata-generation.module";
import {ProjectAvodModule} from "@catmodels/project-avod/project-avod.module";
import {EvidComponent} from './components/evid/evid.component';
import {EvidService} from "@catdetails/services/evid.service";
import {ProjectChecklistModule} from "@catmodels/project-checklist/project-checklist.module";


const imports = [
  CommonModule,
  RouterModule,
  ReactiveFormsModule,
  FormsModule,
  CoreModule,
  UIBaseModule,
  UIFormModule,
  ExplorerModule,
  FeatureUnitsModule,
  SlideInModalModule,
  ConfirmDialogModule,
  DialogModule,
  DataTableModule,
  PaZoneModule,
  ProjectServiceFilesModule,
  ProjectChecklistModule,
  GuiZoneModule,
  ProjectFeatureModule,
  ParametersModule,
  ProjectParametersModule,
  ServicesModule,
  NetworkInterfacesModule,
  ProjectMediaSourcesModule,
  TestRackModule,
  UserdataGenerationModule,
  ProjectOptionTokensModule,
  GgwDevZoneAssignmentModule,
  ProjectEvidFunctionsModule,
  GgwProfilesModule,
  GgwDynamicRoutesModule,
  ProjectPartnumbersModule,
  ProjectSpeakerSetupModule,
  ProjectAvodModule,
  ColorPickerModule,
  InputTextModule,
  TableModule,
  StoreModule.forFeature(
    'projectDetails',
    {
      currentProject: reducers.projectReducer,
      avMatrix: reducers.avMatrixReducer,
      guiControlMatrix: reducers.guiControlMatrixReducer,
      guiZones: reducers.guiZonesReducer,
      ioControlMatrix: reducers.ioControlMatrixReducer,
      projectFeatures: reducers.projectFeaturesReducer,
      properties: reducers.propertiesReducer,
      sbd: reducers.sbdReducer,
      checklist: reducers.checklistReducer,
      psf: reducers.psfReducer,
      parameters: reducers.parametersReducer,
      pms: reducers.pmsReducer,
      ggw: reducers.ggwReducer,
      evid: reducers.evidReducer,
      speakerSetup: reducers.speakerSetupReducer,
      pn: reducers.pnReducer,
      uploadFiles: reducers.uploadFilesReducer,
      testRack: reducers.testRackReducer,
      tokens: reducers.tokenReducer,
      userdata: reducers.userdataGenerationReducer,
      avod: reducers.avodReducer,
    }),
  UserNotesModule

];

const entryComponents = [
  FeatureComponent,
  ProjectUnitsLandingComponent,
  ProjectUnitLandingComponent,
  ProjectFeaturesLandingComponent,
  ProjectParametersLandingComponent,
];

const declarations = [
  ProjectDetailsComponent,
  ProjectPropertiesComponent,
  SwitchLogicSelectComponent,
  ProjectDiagramComponent,
  GuiZonesComponent,
  TestRackComponent,
  FeaturesComponent,
  CreateGuiZoneComponent,
  ProjectUnitsComponent,
  UploadFilesComponent,
  GgwComponent,
  ProjectLopaPositionsComponent,
  EvidComponent,
  UserdataGenerationComponent,
...entryComponents,
];

const providers = [
  ProjectResolve,
  CurrentProjectResolve,
  ProjectDetailsService,
  ProjectPropertiesService,
  SBDService,
  TestRackService,
  ProjectDetailsFeaturesService,
  GuiZoneService,
  GuiZonesService,
  ConfirmationService,
  UploadFilesService,
  UserdataGenerationService,
  GgwService,
  EvidService
];

@NgModule({
  imports: [
    imports,
  ],
  entryComponents: [...entryComponents],
  declarations,
  providers
})
export class ProjectDetailsModule {
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/project-details/project-details.module.ts